/**
 * Runner Status Utility for Public Live Results
 *
 * Thin adapter that maps public-live's interface (isDns) to the
 * canonical shared library (dns) for consistent status calculation.
 */

import {
  calculateRunnerStatus as sharedCalculateRunnerStatus,
  getStatusCssClass,
} from '@as-checkin/shared';
import type {
  StatusEvent,
  StatusStation,
  StatusCode,
  RunnerDisplayStatus,
  RunnerStatusResult as SharedResult,
} from '@as-checkin/shared';

// Re-export types that public-live consumers use
export type { StatusEvent, StatusStation, StatusCode, RunnerDisplayStatus };
export { getStatusCssClass };

/**
 * Input parameters for status calculation (public-live interface).
 * Uses `isDns` instead of `dns` for backwards compatibility.
 */
export interface RunnerStatusInput {
  isDns: boolean;
  events: StatusEvent[];
  raceStatus: 'draft' | 'active' | 'completed';
  stations: StatusStation[];
}

/**
 * Result of status calculation (public-live interface).
 * Subset of the full shared result that public-live actually uses.
 */
export interface RunnerStatusResult {
  statusCode: StatusCode;
  statusLabel: string;
  /** Compact badge label: ENROUTE AS1, AT AS3, ENROUTE FINISH, FINISHED, DNF, DNS, WAITING */
  statusShortLabel: string;
  /** Granular display status (e.g., 'at_station' vs 'enroute') for fine-grained filtering */
  displayStatus: RunnerDisplayStatus;
  isDropped: boolean;
  nextStation?: string;
  nextStationId?: string;
}

/**
 * Calculate runner status using the shared library.
 * Adapts public-live's `isDns` interface to the shared library's `dns` parameter.
 */
export function calculateRunnerStatus(input: RunnerStatusInput): RunnerStatusResult {
  const { isDns, events, raceStatus, stations } = input;

  const result: SharedResult = sharedCalculateRunnerStatus(
    { dns: isDns },
    events,
    raceStatus,
    stations
  );

  return {
    statusCode: result.statusCode,
    statusLabel: result.statusLabel,
    statusShortLabel: buildStatusShortLabel(result, stations),
    displayStatus: result.status,
    isDropped: result.isDropped,
    nextStation: result.nextStation,
    nextStationId: result.nextStationId,
  };
}

/**
 * Build the compact badge label. Numbering matches the shared library's
 * "actual checkpoint" filter so AS{n} aligns with the order runners pass
 * through stations (excludes start, finish, and parent/multi-visit roots).
 */
function buildStatusShortLabel(result: SharedResult, stations: StatusStation[]): string {
  switch (result.status) {
    case 'finished':
      return 'FINISHED';
    case 'dnf':
      return 'DNF';
    case 'dns':
      return 'DNS';
    case 'waiting_for_race_start':
    case 'runner_start_pending':
      return 'WAITING';
    case 'unknown':
      return '—';
  }

  const numbered = stations
    .filter(
      (s) =>
        s.position != null &&
        (s.expected_visits == null || s.expected_visits <= 1) &&
        s.check_mode !== 'start' &&
        s.check_mode !== 'finish'
    )
    .sort((a, b) => a.position - b.position);

  if (result.status === 'at_station') {
    const idx = result.nextStation
      ? numbered.findIndex((s) => s.name === result.nextStation)
      : -1;
    return idx >= 0 ? `AT AS${idx + 1}` : 'ON RACE';
  }

  if (result.status === 'enroute') {
    if (result.statusLabel === 'Enroute to Finish') return 'ENROUTE FINISH';
    let idx = -1;
    if (result.nextStationId) {
      idx = numbered.findIndex((s) => s.id === result.nextStationId);
    }
    if (idx < 0 && result.nextStation) {
      idx = numbered.findIndex((s) => s.name === result.nextStation);
    }
    return idx >= 0 ? `ENROUTE AS${idx + 1}` : 'ON RACE';
  }

  return 'ON RACE';
}
